<template>
  <div class="wat">

    <!-- ═══ quick picks: the questions a reception asks every morning ═══ -->
    <div class="wat-quick">
      <button v-for="chip in quickChips" :key="chip.key" type="button"
              class="wat-chip" :class="{'wat-chip--on': quick === chip.key}" @click="applyQuick(chip)">
        <i :class="chip.icon"/>{{ $t('whatsappHub.quick.' + chip.key) }}
      </button>
    </div>

    <!-- ═══ filter bar ═══ -->
    <div class="wat-bar">
      <div class="wat-seg" role="group">
        <button v-for="seg in segments" :key="seg" type="button"
                class="wat-seg__btn" :class="{'wat-seg__btn--on': segment === seg}" @click="setSegment(seg)">
          {{ $t('whatsappHub.segments.' + seg) }}
        </button>
      </div>

      <div class="wat-range">
        <flat-pickr v-model="from" class="form-control form-control-sm wat-date" :config="dateConfig" @on-change="onRangeChange"/>
        <span class="wat-range__arrow"><i class="fas fa-arrow-right-long"/></span>
        <flat-pickr v-model="to" class="form-control form-control-sm wat-date" :config="dateConfig" @on-change="onRangeChange"/>
      </div>

      <building-select-box v-if="buid" v-model="buildings" :branch-id="buid" append-to-body multiple clearable
                           class="wat-building" :select-props="{placeholder: $t('whatsappHub.building')}" @input="refresh"/>

      <b-input-group size="sm" class="wat-search">
        <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
        <b-form-input v-model="search" :placeholder="$t('whatsappHub.searchPlaceholder')" debounce="150"/>
      </b-input-group>

      <span class="wat-bar__spacer"/>

      <div class="wat-fchips">
        <button type="button" class="wat-fchip wat-fchip--green" :class="{'wat-fchip--on': sessionOnly}" :aria-pressed="sessionOnly"
                v-b-tooltip.hover :title="$t('whatsappHub.filters.sessionOnlyTip')" @click="sessionOnly = !sessionOnly; refresh()">
          <i :class="sessionOnly ? 'fas fa-circle-check' : 'far fa-circle'"/>{{ $t('whatsappHub.filters.sessionOnly') }}
        </button>
        <button type="button" class="wat-fchip wat-fchip--orange" :class="{'wat-fchip--on': neverMessaged}" :aria-pressed="neverMessaged"
                v-b-tooltip.hover :title="$t('whatsappHub.filters.neverMessagedTip')" @click="neverMessaged = !neverMessaged; refresh()">
          <i :class="neverMessaged ? 'fas fa-circle-check' : 'far fa-circle'"/>{{ $t('whatsappHub.filters.neverMessaged') }}
        </button>
      </div>
      <button type="button" class="wat-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refresh">
        <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
      </button>
    </div>

    <!-- ═══ metrics ribbon (click = client-side filter) ═══ -->
    <div class="wat-ops">
      <button v-for="metric in metrics" :key="metric.key" type="button"
              class="wat-metric" :class="{'wat-metric--on': rowFilter === metric.key, 'wat-metric--zero': metric.value === 0}"
              @click="rowFilter = rowFilter === metric.key ? 'all' : metric.key">
        <span class="wat-metric__value tabular" :style="metric.value > 0 && metric.tint ? {color: metric.tint} : null">{{ metric.value }}</span>
        <span class="wat-metric__label">{{ metric.label }}</span>
      </button>
      <span class="wat-ops__range tabular">
        {{ from | moment('DD MMM') }}<template v-if="from !== to"> – {{ to | moment('DD MMM YYYY') }}</template><template v-else> {{ to | moment('YYYY') }}</template>
      </span>
    </div>

    <!-- ═══ list ═══ -->
    <div v-if="loading && rows.length === 0" class="wat-loading">
      <b-spinner small class="mr-50"/>{{ $t('Loading') }}…
    </div>

    <div v-else-if="visibleRows.length === 0" class="wat-empty">
      <i class="fab fa-whatsapp"/>
      <p>{{ $t('whatsappHub.empty.audience') }}</p>
    </div>

    <div v-else class="wat-table">
      <div class="wat-head">
        <label class="wat-cell wat-cell--check">
          <b-form-checkbox :checked="allVisibleSelected" :indeterminate="someVisibleSelected && !allVisibleSelected"
                           @change="toggleAll"/>
        </label>
        <button type="button" class="wat-cell wat-cell--guest wat-sort" @click="sortBy('name')">
          {{ $t('whatsappHub.table.guest') }}<i v-if="sortKey === 'name'" :class="sortDesc ? 'fas fa-caret-down' : 'fas fa-caret-up'"/>
        </button>
        <button type="button" class="wat-cell wat-cell--stay wat-sort" @click="sortBy('checkIn')">
          {{ $t('whatsappHub.table.stay') }}<i v-if="sortKey === 'checkIn'" :class="sortDesc ? 'fas fa-caret-down' : 'fas fa-caret-up'"/>
        </button>
        <button type="button" class="wat-cell wat-cell--wa wat-sort" @click="sortBy('last')">
          {{ $t('whatsappHub.table.whatsapp') }}<i v-if="sortKey === 'last'" :class="sortDesc ? 'fas fa-caret-down' : 'fas fa-caret-up'"/>
        </button>
        <span class="wat-cell wat-cell--actions"/>
      </div>

      <div v-for="row in pagedRows" :key="row.contractId" class="wat-row"
           :class="{'wat-row--selected': isSelected(row), 'wat-row--nophone': !row.phone, 'wat-row--unread': row.unread}">
        <label class="wat-cell wat-cell--check" @click.stop>
          <b-form-checkbox :checked="isSelected(row)" :disabled="!row.phone" @change="toggle(row)"/>
        </label>

        <!-- guest -->
        <div class="wat-cell wat-cell--guest">
          <span class="wat-avatar" :style="avatarStyle(fullName(row))" role="button" @click="$emit('open-chat', row)">
            {{ initials(fullName(row)) }}
            <i v-if="row.unread" class="wat-avatar__dot"/>
          </span>
          <div class="wat-guest">
            <button type="button" class="wat-name" @click="$emit('open-chat', row)">{{ fullName(row) }}</button>
            <span v-if="row.phone" class="wat-phone tabular">{{ row.phone }}</span>
            <span v-else class="wat-phone wat-phone--missing"><i class="fas fa-phone-slash mr-25"/>{{ $t('whatsappHub.table.noPhone') }}</span>
            <span v-if="row.lang" class="wat-lang">{{ row.lang }}</span>
          </div>
        </div>

        <!-- stay -->
        <div class="wat-cell wat-cell--stay">
          <span class="wat-address" :title="row.propertyAddress"><i class="fas fa-home mr-25"/>{{ row.propertyAddress || '—' }}</span>
          <span class="wat-dates tabular">
            <b :class="{'wat-dates--today': isToday(row.checkIn)}">{{ row.checkIn | moment('DD.MM') }}</b>
            <i class="fas fa-arrow-right-long"/>
            <b :class="{'wat-dates--today': isToday(row.checkOut)}">{{ row.checkOut | moment('DD.MM') }}</b>
            <span v-if="row.nights != null" class="wat-muted">· {{ row.nights }} {{ $t('whatsappHub.table.nights') }}</span>
            <span v-if="row.numberOfGuests" class="wat-muted">· {{ row.numberOfGuests }} <i class="fas fa-user-group"/></span>
          </span>
          <span class="wat-stay-meta">
            <span v-if="row.platform" class="wat-platform">{{ row.platform }}</span>
            <span v-if="row.reservationId" class="wat-muted tabular">#{{ row.reservationId }}</span>
            <span v-if="row.checkInTime" class="wat-muted tabular"><i class="far fa-clock"/> {{ row.checkInTime }}</span>
          </span>
        </div>

        <!-- whatsapp state -->
        <div class="wat-cell wat-cell--wa">
          <span class="wat-session" :class="row.sessionOpen ? 'wat-session--open' : 'wat-session--closed'"
                v-b-tooltip.hover :title="row.sessionOpen ? $t('whatsappHub.table.sessionOpenTip') : $t('whatsappHub.table.sessionClosedTip')">
            <i/>{{ row.sessionOpen ? $t('whatsappHub.table.sessionOpen', {time: timeLeft(row.sessionExpiresAt)}) : $t('whatsappHub.table.templateRequired') }}
          </span>
          <span v-if="row.lastMessage" class="wat-last" :title="row.lastMessage.body">
            <i :class="row.lastMessage.isIncoming ? 'fas fa-arrow-down wat-last__dir wat-last__dir--in' : 'fas fa-arrow-up wat-last__dir'"/>
            <i v-if="!row.lastMessage.isIncoming" :class="statusMeta(row.lastMessage.status).icon"
               class="wat-tick" :data-tone="statusMeta(row.lastMessage.status).tone"
               v-b-tooltip.hover :title="$t('whatsappHub.status.' + statusMeta(row.lastMessage.status).key)"/>
            <span class="wat-last__text">{{ preview(row.lastMessage) }}</span>
            <span class="wat-last__ago">{{ ago(row.lastMessage.date) }}</span>
          </span>
          <span v-else class="wat-last wat-last--none">{{ $t('whatsappHub.table.noMessages') }}</span>
          <span v-if="row.templatesSent && row.templatesSent.length" class="wat-templates">
            <span v-for="name in row.templatesSent.slice(0, 3)" :key="name" class="wat-template-chip" :title="name">
              <i class="fas fa-file-lines"/>{{ name }}
            </span>
            <span v-if="row.templatesSent.length > 3" class="wat-template-chip wat-template-chip--more">+{{ row.templatesSent.length - 3 }}</span>
          </span>
        </div>

        <!-- actions -->
        <div class="wat-cell wat-cell--actions">
          <button type="button" class="wat-action" v-b-tooltip.hover :title="$t('whatsappHub.actions.chat')" @click="$emit('open-chat', row)">
            <i class="far fa-comments"/>
          </button>
          <button type="button" class="wat-action" v-b-tooltip.hover :title="$t('whatsappHub.actions.reservation')" @click="$emit('open-contract', row.contractId)">
            <i class="fa-solid fa-file-contract"/>
          </button>
          <button type="button" class="wat-action wat-action--send" :disabled="!row.phone"
                  v-b-tooltip.hover :title="$t('whatsappHub.actions.send')" @click="$emit('compose', [row])">
            <i class="fas fa-paper-plane"/>
          </button>
        </div>
      </div>

      <div v-if="pagedRows.length < visibleRows.length" class="wat-more">
        <b-button variant="outline-secondary" size="sm" @click="limit += 50">
          {{ $t('whatsappHub.showMore', {n: visibleRows.length - pagedRows.length}) }}
        </b-button>
      </div>
    </div>

    <!-- ═══ selection bar ═══ -->
    <transition name="wat-rise">
      <div v-if="selectedRows.length > 0" class="wat-sel">
        <span class="wat-sel__count"><b class="tabular">{{ selectedRows.length }}</b> {{ $t('whatsappHub.actions.selected') }}</span>
        <span v-if="selectedNotInSession > 0" class="wat-sel__hint">
          <i class="fas fa-circle-info mr-25"/>{{ $t('whatsappHub.actions.notInSession', {n: selectedNotInSession}) }}
        </span>
        <span class="wat-sel__spacer"/>
        <b-button variant="flat-secondary" size="sm" @click="clearSelection">{{ $t('whatsappHub.actions.clear') }}</b-button>
        <b-button variant="outline-success" size="sm" :disabled="selectedNotInSession > 0"
                  v-b-tooltip.hover :title="selectedNotInSession > 0 ? $t('whatsappHub.actions.textNeedsSession') : ''"
                  @click="$emit('compose', selectedRows, {mode: 'TEXT'})">
          <i class="fas fa-keyboard mr-25"/>{{ $t('whatsappHub.actions.sendText') }}
        </b-button>
        <b-button variant="success" size="sm" @click="$emit('compose', selectedRows, {mode: 'TEMPLATE'})">
          <i class="fas fa-file-lines mr-25"/>{{ $t('whatsappHub.actions.sendTemplate') }}
        </b-button>
      </div>
    </transition>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import flatPickr from 'vue-flatpickr-component'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import {avatarStyle, fullName, initials, statusMeta} from './whatsappUtils'

const SEGMENTS = ['ARRIVING', 'DEPARTING', 'IN_HOUSE', 'BOOKED'];

export default {
  name: 'WhatsappAudienceTable',
  components: {flatPickr, BuildingSelectBox},
  props: {
    buid: {type: [Number, String], default: null},
    status: {type: Object, default: null},
  },
  data() {
    const today = this.$moment().format('YYYY-MM-DD');
    return {
      segments: SEGMENTS,
      segment: 'ARRIVING',
      from: today,
      to: today,
      quick: 'arrivingToday',
      buildings: [],
      search: '',
      sessionOnly: false,
      neverMessaged: false,
      rowFilter: 'all',
      rows: [],
      summary: {},
      loading: false,
      selected: {},
      sortKey: 'checkIn',
      sortDesc: false,
      limit: 50,
      now: this.$moment(),
      ticker: null,
      dateConfig: {enableTime: false, dateFormat: 'Y-m-d', allowInput: true},
      pendingRequest: 0,
    }
  },
  computed: {
    quickChips() {
      return [
        {key: 'arrivingToday', icon: 'fas fa-plane-arrival', segment: 'ARRIVING', from: 0, to: 0},
        {key: 'arrivingTomorrow', icon: 'fas fa-plane-arrival', segment: 'ARRIVING', from: 1, to: 1},
        {key: 'arrivingWeek', icon: 'fas fa-calendar-week', segment: 'ARRIVING', from: 0, to: 6},
        {key: 'inHouse', icon: 'fas fa-bed', segment: 'IN_HOUSE', from: 0, to: 0},
        {key: 'departingToday', icon: 'fas fa-plane-departure', segment: 'DEPARTING', from: 0, to: 0},
        {key: 'departingTomorrow', icon: 'fas fa-plane-departure', segment: 'DEPARTING', from: 1, to: 1},
        {key: 'bookedToday', icon: 'fas fa-calendar-plus', segment: 'BOOKED', from: 0, to: 0},
      ];
    },
    metrics() {
      const s = this.summary || {};
      return [
        {key: 'all', value: s.total || 0, label: this.$t('whatsappHub.summary.total')},
        {key: 'sessionOpen', value: s.sessionOpen || 0, label: this.$t('whatsappHub.summary.sessionOpen'), tint: '#1f9d57'},
        {key: 'unread', value: s.unread || 0, label: this.$t('whatsappHub.summary.unread'), tint: '#7367f0'},
        {key: 'neverMessaged', value: s.neverMessaged || 0, label: this.$t('whatsappHub.summary.neverMessaged'), tint: '#ff9f43'},
        {key: 'messagedToday', value: s.messagedToday || 0, label: this.$t('whatsappHub.summary.messagedToday')},
        {key: 'noPhone', value: s.noPhone || 0, label: this.$t('whatsappHub.summary.noPhone'), tint: '#ea5455'},
      ];
    },
    filteredRows() {
      const q = (this.search || '').trim().toLowerCase();
      return this.rows.filter(row => {
        switch (this.rowFilter) {
          case 'sessionOpen': if (!row.sessionOpen) return false; break;
          case 'unread': if (!row.unread) return false; break;
          case 'neverMessaged': if (row.messageCount > 0) return false; break;
          case 'messagedToday': if (!row.sentToday) return false; break;
          case 'noPhone': if (row.phone) return false; break;
          default:
        }
        if (q === '') return true;
        const hay = [fullName(row), row.phone, row.propertyAddress, row.reservationId, row.email].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    },
    visibleRows() {
      const rows = this.filteredRows.slice();
      const dir = this.sortDesc ? -1 : 1;
      rows.sort((a, b) => {
        let va, vb;
        if (this.sortKey === 'name') { va = fullName(a).toLowerCase(); vb = fullName(b).toLowerCase(); }
        else if (this.sortKey === 'last') { va = a.lastMessage ? a.lastMessage.date : ''; vb = b.lastMessage ? b.lastMessage.date : ''; }
        else { va = (a.checkIn || '') + (a.propertyAddress || ''); vb = (b.checkIn || '') + (b.propertyAddress || ''); }
        return va < vb ? -dir : va > vb ? dir : 0;
      });
      return rows;
    },
    pagedRows() {
      return this.visibleRows.slice(0, this.limit);
    },
    selectedRows() {
      return Object.values(this.selected);
    },
    selectedNotInSession() {
      return this.selectedRows.filter(r => !r.sessionOpen).length;
    },
    allVisibleSelected() {
      const selectable = this.pagedRows.filter(r => r.phone);
      return selectable.length > 0 && selectable.every(r => this.selected[r.contractId] != null);
    },
    someVisibleSelected() {
      return this.pagedRows.some(r => this.selected[r.contractId] != null);
    },
  },
  watch: {
    buid() {
      this.clearSelection();
      this.refresh();
    },
  },
  created() {
    this.refresh();
    this.ticker = setInterval(() => this.now = this.$moment(), 60000);
  },
  beforeDestroy() {
    clearInterval(this.ticker);
  },
  methods: {
    ...mapActions('whatsapp', ['hubAudience']),
    initials, avatarStyle, fullName, statusMeta,

    applyQuick(chip) {
      this.quick = chip.key;
      this.segment = chip.segment;
      this.from = this.$moment().add(chip.from, 'days').format('YYYY-MM-DD');
      this.to = this.$moment().add(chip.to, 'days').format('YYYY-MM-DD');
      this.refresh();
    },
    setSegment(segment) {
      this.segment = segment;
      this.quick = null;
      this.refresh();
    },
    onRangeChange() {
      this.quick = null;
      this.$nextTick(this.refresh);
    },
    refresh() {
      if (!this.buid) return;
      const requestId = ++this.pendingRequest;
      this.loading = true;
      this.hubAudience({
        buid: this.buid,
        segment: this.segment,
        from: this.from,
        to: this.to,
        buildings: this.buildings && this.buildings.length ? this.buildings : null,
        sessionOnly: this.sessionOnly,
        neverMessaged: this.neverMessaged,
      }).then(ans => {
        if (requestId !== this.pendingRequest) return;
        this.rows = ans.rows || [];
        this.summary = ans.summary || {};
        this.limit = 50;
        // drop selections that left the list
        const ids = new Set(this.rows.map(r => r.contractId));
        Object.keys(this.selected).forEach(id => { if (!ids.has(id)) this.$delete(this.selected, id); });
        this.$emit('loaded', {rows: this.rows, summary: this.summary});
      }).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => {
        if (requestId === this.pendingRequest) this.loading = false;
      });
    },

    isSelected(row) {
      return this.selected[row.contractId] != null;
    },
    toggle(row) {
      if (this.selected[row.contractId] != null) this.$delete(this.selected, row.contractId);
      else if (row.phone) this.$set(this.selected, row.contractId, row);
    },
    toggleAll(checked) {
      this.pagedRows.forEach(row => {
        if (!row.phone) return;
        if (checked) this.$set(this.selected, row.contractId, row);
        else this.$delete(this.selected, row.contractId);
      });
    },
    clearSelection() {
      this.selected = {};
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortDesc = !this.sortDesc;
      else { this.sortKey = key; this.sortDesc = false; }
    },

    isToday(date) {
      return date != null && this.$moment(date).isSame(this.$moment(), 'day');
    },
    timeLeft(expiresAt) {
      if (!expiresAt) return '';
      const minutes = this.$moment(expiresAt).diff(this.now, 'minutes');
      if (minutes <= 0) return '0m';
      const h = Math.floor(minutes / 60), m = minutes % 60;
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    },
    ago(date) {
      const m = this.$moment(date);
      if (!m.isValid()) return '';
      return m.isSame(this.now, 'day') ? m.format('HH:mm') : m.fromNow();
    },
    preview(message) {
      if (!message) return '';
      if (message.type === 'MEDIA' && !message.body) return '📎 ' + this.$t('whatsappHub.thread.media');
      const text = String(message.body || '').replace(/<[^>]+>/g, '');
      return text.length > 90 ? text.substring(0, 90) + '…' : text;
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wat { position: relative; }

/* quick chips */
.wat-quick { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.wat-chip {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 999px; background: #fff;
  color: #475569; font-size: .74rem; font-weight: 700; padding: 6px 12px; cursor: pointer;
  transition: all .15s;
  i { color: #94a3b8; font-size: .7rem; }
  &:hover { border-color: rgba(37, 211, 102, .5); color: #1f9d57; }
  &:focus { outline: none; }
  &--on { background: rgba(37, 211, 102, .14); border-color: rgba(37, 211, 102, .6); color: #1f9d57; i { color: #1f9d57; } }
}

/* filter bar */
.wat-bar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.wat-bar__spacer { flex: 1; }
.wat-seg { display: inline-flex; border: 1px solid rgba(15, 23, 42, .1); border-radius: 10px; overflow: hidden; background: #fff; }
.wat-seg__btn {
  border: none; background: transparent; padding: 6px 12px; cursor: pointer;
  font-size: .72rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #64748b;
  &:focus { outline: none; }
  & + & { border-left: 1px solid rgba(15, 23, 42, .08); }
  &--on { background: #1f2a44; color: #fff; }
}
.wat-range { display: inline-flex; align-items: center; gap: 4px; }
.wat-date { width: 112px; text-align: center; }
.wat-range__arrow { color: #94a3b8; font-size: .7rem; }
.wat-building { min-width: 180px; }
.wat-search { width: 220px; }
.wat-fchips { display: inline-flex; gap: 6px; }
.wat-fchip {
  display: inline-flex; align-items: center; gap: 7px; height: 32px; padding: 0 12px; cursor: pointer;
  border: 1px dashed rgba(15, 23, 42, .18); border-radius: 999px; background: transparent;
  font-size: .72rem; font-weight: 700; color: #64748b; white-space: nowrap; transition: all .15s;
  i { font-size: .8rem; color: #cbd5e1; }
  &:hover { border-color: rgba(15, 23, 42, .35); color: #1f2a44; }
  &:focus { outline: none; }
  &--on { border-style: solid; }
  &--green.wat-fchip--on { background: rgba(37, 211, 102, .14); border-color: rgba(37, 211, 102, .6); color: #1f9d57; i { color: #1f9d57; } }
  &--orange.wat-fchip--on { background: rgba(255, 159, 67, .14); border-color: rgba(255, 159, 67, .6); color: #c77400; i { color: #ff9f43; } }
}
.wat-tool {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  &:hover { background: rgba(37, 211, 102, .16); color: #1f9d57; }
  &:focus { outline: none; }
}

/* metrics */
.wat-ops { display: flex; align-items: stretch; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.wat-metric {
  display: flex; flex-direction: column; align-items: flex-start; min-width: 92px;
  border: 1px solid transparent; border-radius: 12px; background: rgba(15, 23, 42, .03);
  padding: 8px 12px; cursor: pointer; text-align: left; transition: all .15s;
  &:hover { background: rgba(15, 23, 42, .06); }
  &:focus { outline: none; }
  &--on { border-color: rgba(37, 211, 102, .6); background: rgba(37, 211, 102, .1); }
  &--zero .wat-metric__value { color: #cbd5e1; }
}
.wat-metric__value { font-size: 1.25rem; font-weight: 800; line-height: 1.1; color: #1f2a44; }
.wat-metric__label { font-size: .64rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; margin-top: 2px; }
.wat-ops__range { align-self: center; margin-left: auto; font-size: .78rem; font-weight: 700; color: #64748b; }

/* table */
.wat-loading { text-align: center; padding: 40px 0; color: #64748b; }
.wat-empty {
  text-align: center; padding: 48px 0 30px; color: #94a3b8;
  i { font-size: 2.4rem; opacity: .35; }
  p { margin-top: 8px; font-size: .86rem; }
}
.wat-table { display: flex; flex-direction: column; }
.wat-head, .wat-row { display: grid; grid-template-columns: 36px minmax(200px, 1.1fr) minmax(220px, 1.3fr) minmax(240px, 1.4fr) 112px; align-items: center; gap: 10px; }
.wat-head { padding: 4px 8px; border-bottom: 1px solid rgba(15, 23, 42, .08); }
.wat-sort {
  border: none; background: none; padding: 4px 0; text-align: left; cursor: pointer;
  font-size: .64rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8;
  i { margin-left: 4px; }
  &:focus { outline: none; }
}
.wat-row {
  padding: 10px 8px; border-radius: 12px; border-bottom: 1px solid rgba(15, 23, 42, .05); transition: background .15s;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(37, 211, 102, .05); }
  &--selected { background: rgba(37, 211, 102, .1); }
  &--nophone { opacity: .72; }
}
.wat-cell { min-width: 0; }
.wat-cell--check { margin: 0; display: flex; align-items: center; }
.wat-cell--guest { display: flex; align-items: center; gap: 10px; }
.wat-cell--stay, .wat-cell--wa { display: flex; flex-direction: column; gap: 3px; }
.wat-cell--actions { display: inline-flex; gap: 6px; justify-content: flex-end; }

.wat-avatar {
  position: relative; width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .76rem; font-weight: 800; color: #fff; user-select: none;
}
.wat-avatar__dot { position: absolute; right: -1px; top: -1px; width: 10px; height: 10px; border-radius: 50%; background: #7367f0; border: 2px solid #fff; }
.wat-guest { display: flex; flex-direction: column; min-width: 0; }
.wat-name {
  border: none; background: none; padding: 0; text-align: left; cursor: pointer;
  color: #1f2a44; font-size: .86rem; font-weight: 700; text-transform: capitalize;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  &:hover { color: #1f9d57; text-decoration: underline; }
  &:focus { outline: none; }
}
.wat-row--unread .wat-name { font-weight: 800; }
.wat-phone { font-size: .72rem; color: #94a3b8; &--missing { color: #ea5455; font-weight: 700; } }
.wat-lang { font-size: .6rem; font-weight: 800; text-transform: uppercase; color: #94a3b8; letter-spacing: .06em; }

.wat-address { font-size: .78rem; color: #475569; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; i { color: #94a3b8; } }
.wat-dates { font-size: .76rem; color: #1f2a44; display: inline-flex; align-items: center; gap: 5px; flex-wrap: wrap; i { color: #94a3b8; font-size: .62rem; } }
.wat-dates--today { color: #1f9d57; }
.wat-muted { color: #94a3b8; font-weight: 500; font-size: .72rem; }
.wat-stay-meta { display: inline-flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.wat-platform { font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; padding: 2px 7px; border-radius: 999px; background: rgba(115, 103, 240, .12); color: #7367f0; }

.wat-session {
  display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
  padding: 3px 9px; border-radius: 999px; cursor: default;
  i { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
  &--open { background: rgba(37, 211, 102, .14); color: #1f9d57; i { background: #25d366; box-shadow: 0 0 0 3px rgba(37, 211, 102, .2); } }
  &--closed { background: rgba(255, 159, 67, .14); color: #c77400; i { background: #ff9f43; } }
}
.wat-last { display: inline-flex; align-items: center; gap: 6px; font-size: .76rem; color: #475569; min-width: 0; &--none { color: #cbd5e1; font-style: italic; } }
.wat-last__dir { font-size: .6rem; color: #94a3b8; &--in { color: #1f9d57; } }
.wat-last__text { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wat-last__ago { font-size: .66rem; color: #94a3b8; white-space: nowrap; }
.wat-tick { font-size: .68rem; &[data-tone="ok"] { color: #94a3b8; } &[data-tone="read"] { color: #34b7f1; } &[data-tone="bad"] { color: #ea5455; } &[data-tone="muted"] { color: #cbd5e1; } }
.wat-templates { display: inline-flex; flex-wrap: wrap; gap: 4px; }
.wat-template-chip {
  display: inline-flex; align-items: center; gap: 4px; max-width: 160px;
  font-size: .62rem; font-weight: 700; padding: 2px 7px; border-radius: 6px;
  background: rgba(15, 23, 42, .05); color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  i { font-size: .58rem; opacity: .7; }
  &--more { background: transparent; }
}
.wat-action {
  width: 28px; height: 28px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; color: #64748b; font-size: .72rem; transition: all .15s;
  &:hover { border-color: #7367f0; color: #7367f0; background: rgba(115, 103, 240, .08); }
  &:focus { outline: none; }
  &:disabled { opacity: .4; cursor: not-allowed; }
  &--send:hover { border-color: #28c76f; color: #1f9d57; background: rgba(40, 199, 111, .1); }
}
.wat-more { text-align: center; padding: 12px 0 4px; }

/* selection bar */
.wat-sel {
  position: sticky; bottom: 12px; z-index: 5; margin-top: 12px;
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  background: #1f2a44; color: #fff; border-radius: 14px; padding: 10px 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, .3);
}
.wat-sel__count { font-size: .84rem; b { font-size: 1rem; } }
.wat-sel__hint { font-size: .72rem; color: #ffd18b; }
.wat-sel__spacer { flex: 1; }
.wat-rise-enter-active, .wat-rise-leave-active { transition: all .2s ease; }
.wat-rise-enter, .wat-rise-leave-to { opacity: 0; transform: translateY(10px); }

@media (max-width: 992px) {
  .wat-head { display: none; }
  .wat-row { grid-template-columns: 36px 1fr; }
  .wat-cell--stay, .wat-cell--wa { grid-column: 2; }
  .wat-cell--actions { grid-column: 2; justify-content: flex-start; }
}
</style>

<style lang="scss">
.dark-layout {
  .wat-chip { background: #283046; border-color: rgba(255, 255, 255, .12); color: #b4b7bd; }
  .wat-chip--on { background: rgba(37, 211, 102, .16); border-color: rgba(37, 211, 102, .5); color: #4ade80; }
  .wat-seg { background: #283046; border-color: rgba(255, 255, 255, .12); }
  .wat-seg__btn { color: #94a3b8; &--on { background: #7367f0; color: #fff; } }
  .wat-fchip { border-color: rgba(255, 255, 255, .2); color: #94a3b8; i { color: #4b5261; } &:hover { color: #e9ecf2; border-color: rgba(255, 255, 255, .4); } }
  .wat-fchip--green.wat-fchip--on { color: #4ade80; i { color: #4ade80; } }
  .wat-fchip--orange.wat-fchip--on { color: #ffb15c; i { color: #ffb15c; } }
  .wat-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wat-metric { background: rgba(255, 255, 255, .04); &:hover { background: rgba(255, 255, 255, .08); } }
  .wat-metric__value { color: #e9ecf2; }
  .wat-metric--zero .wat-metric__value { color: #4b5261; }
  .wat-head { border-color: rgba(255, 255, 255, .08); }
  .wat-row { border-color: rgba(255, 255, 255, .06); &:hover { background: rgba(37, 211, 102, .08); } &--selected { background: rgba(37, 211, 102, .14); } }
  .wat-name { color: #e9ecf2; }
  .wat-avatar__dot { border-color: #283046; }
  .wat-address { color: #b4b7bd; }
  .wat-dates { color: #e9ecf2; }
  .wat-last { color: #b4b7bd; &--none { color: #4b5261; } }
  .wat-template-chip { background: rgba(255, 255, 255, .08); color: #b4b7bd; }
  .wat-action { background: #283046; border-color: rgba(255, 255, 255, .12); color: #94a3b8; }
  .wat-sel { background: #161d31; box-shadow: 0 10px 30px rgba(0, 0, 0, .5); }
}
</style>
